import type { ThemeConfig } from "@/types/theme";

export const tailwindYellow: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-yellow-700 dark:text-yellow-400",
    secondary: "text-yellow-900 dark:text-yellow-500",
    accent: "text-lime-600 dark:text-lime-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-yellow-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-yellow-600 dark:bg-yellow-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-yellow-50 to-lime-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-yellow-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-yellow-600 dark:bg-yellow-500",
    backgroundSecondaryColor: "bg-yellow-800 dark:bg-yellow-700",
    backgroundAccentColor: "bg-lime-500 dark:bg-lime-400",
    backgroundPrimarySubtle: "bg-yellow-50 dark:bg-yellow-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-yellow-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-yellow-700 dark:text-yellow-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-yellow-600 dark:text-yellow-400",
    iconBackgroundFilled: "bg-yellow-100 dark:bg-yellow-950",
    iconBackgroundGradient: "bg-gradient-to-br from-yellow-500 to-lime-500",
    iconBackgroundDefault: "bg-yellow-50 dark:bg-yellow-950/50",
    iconBorder: "border-yellow-200 dark:border-yellow-800",

    // Border classes
    borderDefault: "border-yellow-200 dark:border-gray-700",
    borderLight: "border-yellow-100 dark:border-gray-800",
    borderStrong: "border-yellow-300 dark:border-gray-600",
    borderPrimary: "border-yellow-700 dark:border-yellow-500",
    borderSecondary: "border-yellow-900 dark:border-yellow-700",
    borderBottom: "border-b border-yellow-200 dark:border-gray-700",
    borderAccent: "border-yellow-600 dark:border-yellow-400",

    // Badge classes
    badgeBackground: "bg-yellow-100 dark:bg-yellow-950",
    badgeText: "text-yellow-700 dark:text-yellow-300",

    // Shadow classes
    shadowPrimary: "shadow-yellow-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-yellow-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(234,179,8,0.30)]", // yellow-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-yellow-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-yellow-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-yellow-400 dark:bg-yellow-600",
    textEmphasis: "text-indigo-600 dark:text-indigo-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-yellow-600 to-yellow-700",
    gradientSecondary: "bg-gradient-to-br from-yellow-700 to-yellow-900",
    gradientAccent: "bg-gradient-to-r from-yellow-500 to-lime-500",
    gradientSubtle:
      "bg-gradient-to-br from-yellow-50 to-lime-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-yellow-600 via-lime-500 to-yellow-600",
    gradientHero:
      "bg-gradient-to-br from-yellow-600 via-lime-500 to-yellow-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
